import * as React from 'react';
import { useContext, useMemo, useState } from 'react';
import { DragAndDrop, FileProps, imageFileComponent } from '../../shared/DragAndDrop';
import { formatSizeUnits, typeFileComponent } from '../../../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle } from '@fortawesome/free-regular-svg-icons';
import { AppAgentContext } from '../../../context/ContextProvider';
import { useTranslate } from 'sbx-crm-menu-ui';
import RenderFormFieldsComponent, {
    GroupTypes,
} from '../../shared/renderFormFieldsComponent/RenderFormFieldsComponent';
import { AiAgentV2Data } from '../../../types/AiAgentV2';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../ui/accordion';
import { ClipboardPenLine, Plus, RotateCcw, Settings } from 'lucide-react';
import RenderFormFieldComponent from '../../shared/renderFormFieldsComponent/RenderFormFieldComponent';
import { DocumentForm, DocumentSetupContext } from './DocumentPageComponent';
import DynamicInputList from '../../shared/DynamicInputListComponent';
import { appActions } from '../../../store';
import { ModalType } from '../../../types/Modal';
import { createFolderService, deleteFileService, uploadFileCompressedService } from '../../../services/AssetService';
import { useNavigate, useParams } from 'react-router-dom';
import ButtonComponent from '../../shared/ButtonComponent';
import { v4 as uuidv4 } from 'uuid';
import { upsertDocumentService } from '../../../services/DocumentService';
import { toast } from 'sonner';
import { SubmitHandler } from 'react-hook-form';

const classNameLabel = 'form-input-label';

type Props = {
    setFilesComponent: React.Dispatch<React.SetStateAction<FileProps[]>>;
    setFiles: React.Dispatch<React.SetStateAction<File[]>>;
    files: File[];
    filesComponent: FileProps[];
    getDocument: () => void
    getFiles: () => void
}

const DocumentSetupComponent: React.FC<Props> = ({
                                                     filesComponent,
                                                     setFiles,
                                                     files,
                                                     setFilesComponent,
                                                     getDocument, getFiles,
                                                 }) => {

    const {
        state: { session: { ai_agent_config: { file_types = ['pdf'], folders: { documents = '' } } } },
        dispatch,
    } = useContext(AppAgentContext);
    const { id } = useParams();
    const navigate = useNavigate();
    const { t: common } = useTranslate('common');
    const { t: customMessage } = useTranslate('custom-message');
    const { t: documentT } = useTranslate('document');
    const [loading, setLoading] = useState(false);
    const [classificationInput, setClassificationInput] = useState<string>('');
    const [examplesInput, setExamplesInput] = useState<string>('');
    const { control, watch, setValue, document, handleSubmit } = useContext(DocumentSetupContext);

    const basicFields = useMemo((): GroupTypes<keyof AiAgentV2Data> => ({
        name: {
            columns: 'grid-cols-1 mb-3',
            items: [
                {
                    name: 'document.name',
                    label: common('name'),
                    classNameLabel,
                    placeholder: common('name'),
                    rules: {
                        required: true,
                    },
                },
            ],
        },
        text_setup: {
            columns: 'grid-cols-1 gap-3',
            items: [
                {
                    name: 'document.config.vs_config.chunk_size',
                    label: common('chunk_size'),
                    classNameLabel,
                    placeholder: common('chunk_size'),
                    type: 'number',
                    rules: {
                        required: true,
                    },
                },
                {
                    name: 'document.config.vs_config.chunk_overlap',
                    label: common('chunk_overlap'),
                    classNameLabel,
                    placeholder: common('chunk_overlap'),
                    type: 'number',
                    rules: {
                        required: true,
                    },
                },
            ],
        },
    }), [common]);

    const classificationList = watch('document.config.doc_analysis_params.contract_analysis_classification') || [];
    const examplesList = watch('document.config.doc_analysis_params.contract_analysis_examples') || [];

    const handleRemoveFile = (fileToRemove: FileProps) => {

        if (id && id !== 'create') {
            dispatch(appActions.openModal({
                type: ModalType.CONFIRMATION,
                data: {
                    title: common('file'),
                    description: `${customMessage('warn-delete')} ${common('the1')} ${common('file')}?`,
                    async onConfirm() {
                        const res = await deleteFileService(fileToRemove.key ?? '0');
                        if (res?.success) {
                            getFiles();
                            toast.success(common('success_delete_message'));
                            dispatch(appActions.closeModal(ModalType.CONFIRMATION));
                        }
                    },
                    onClose() {
                        dispatch(appActions.closeModal(ModalType.CONFIRMATION));
                    },
                },
            }));
        } else {
            setFilesComponent((prevFiles) => prevFiles.filter((file) => file.name !== fileToRemove.name));
            setFiles((prevFiles) => prevFiles.filter((file) => file.name !== fileToRemove.name));
        }
    };

    const upsertDocument: SubmitHandler<DocumentForm> = async (data) => {
        dispatch(appActions.openModal({
            type: ModalType.CONFIRMATION,
            data: {
                title: common('file'),
                description: `${customMessage('warn-save')} ${common('the1')} ${common('document')}?`,
                async onConfirm() {
                    dispatch(appActions.closeModal(ModalType.CONFIRMATION));
                    const document = data.document;

                    setLoading(true);

                    if (files.length > 0) {
                        if (document && document.source?.length > 0) {
                            const res = await uploadFileCompressedService(document.source, files);
                            if (res.success) {
                                const itemsKey = typeFileComponent(filesComponent, res.items);
                                setFilesComponent(itemsKey);
                            }
                        } else {
                            const resp = await createFolderService(documents, uuidv4());
                            if (resp) {
                                const folderKey = resp.folder.key;
                                setValue('document.source', folderKey);
                                const res = await uploadFileCompressedService(folderKey, files);
                                if (res.success) {
                                    const itemsKey = typeFileComponent(filesComponent, res.items);
                                    setFilesComponent(itemsKey);
                                }
                            }
                        }
                    }

                    if (id && id !== 'create') {
                        document.id = parseInt(id);
                    }


                    const res = await upsertDocumentService(document);
                    if (res?.success && res?.item?.id) {
                        if (id && id === 'create') {
                            navigate(`/document_detail_v2/${res.item.id}`);
                        }else{
                            getDocument();
                        }
                    } else {
                        toast.error(res?.message || 'Error');
                    }
                    setLoading(false);

                },
                onClose() {
                    dispatch(appActions.closeModal(ModalType.CONFIRMATION));
                },
            },
        }));
    };

    React.useEffect(() => {

        if (files.length > 0) {
            if (document && document.getSource()?.length > 0) {
                dispatch(appActions.openModal({
                    type: ModalType.CONFIRMATION,
                    data: {
                        title: common('file'),
                        description: `${customMessage('warn-save')} ${common('the1')} ${common('document')}?`,
                        async onConfirm() {
                            dispatch(appActions.closeModal(ModalType.CONFIRMATION));
                            const res = await uploadFileCompressedService(document.getSource(), files as File[]);
                            if (res?.success) {
                                getFiles();
                            }
                        },
                        onClose() {
                            dispatch(appActions.closeModal(ModalType.CONFIRMATION));
                            setFiles([]);
                        },
                    },
                }));
            }
        }

    }, [files]);

    return (
        <Card className="bg-gradient-to-br from-pink-100 to-purple-100 shadow-xl">
            <CardHeader className="flex !flex-row items-center justify-between">
                <CardTitle className="text-2xl font-bold text-purple-800">{common('document')}</CardTitle>

                <ButtonComponent
                    loading={loading}
                    disabled={loading}
                    onClick={() => handleSubmit(upsertDocument)()}
                    label={<>
                        {id && id !== 'create' ? <RotateCcw size={20} className="mr-2" /> : <Plus size={20}
                                                                                                  className="mr-2" />} {`${common(id && id !== 'create' ? 'update' : 'create')} ${common('document')}`}
                    </>}
                    className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold py-2 px-4 rounded-full transition-all duration-300 transform hover:scale-105"
                />
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-1">
                <div className="flex flex-col gap-1">
                    <RenderFormFieldsComponent fields={basicFields.name.items ?? []} control={control} />

                    <Card>
                        <CardContent className="py-3">
                            <div className="grid grid-cols-1 gap-1 items-center mb-3">
                                <label className={classNameLabel}>{common('upload_file')}</label>
                                <DragAndDrop
                                    fileTypes={file_types}
                                    setFiles={setFiles}
                                    setFilesComponent={files => {
                                        if (id && id === 'create') {
                                            setFilesComponent(files);
                                        }
                                    }}
                                />
                            </div>
                            <div className="grid grid-cols-1 gap-1 items-center mb-5">
                                <h2 className={classNameLabel}>{common('files')}</h2>
                                <div className="flex flex-wrap gap-5 my-2">
                                    {filesComponent.length > 0 ? (
                                        filesComponent.map((file, index) => (
                                            <div key={index} className="relative flex gap-5">
                                                <div>{imageFileComponent(file)}</div>
                                                <div>
                                                    <p>{file.name}</p>
                                                    <p>{formatSizeUnits(file.size)}</p>
                                                </div>
                                                <div>
                          <span
                              className="cursor-pointer text-red-400 w-5 h-5 flex justify-center items-center"
                              onClick={() => handleRemoveFile(file)}
                          >
                            <FontAwesomeIcon icon={faTimesCircle as any} />
                          </span>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-gray-300">{common('no_files')}</p>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="flex flex-col gap-1">
                    <Card>
                        <CardContent className="py-0">
                            <Accordion type="single" collapsible className="w-full">
                                <AccordionItem value="text-setup" className="border-b-0">
                                    <AccordionTrigger
                                        className="text-lg font-semibold text-purple-700 hover:text-purple-900">
                                        <div className="flex items-center capitalize">
                                            <ClipboardPenLine className="w-5 h-5 mr-2" />
                                            {common('setup_text')}
                                        </div>
                                    </AccordionTrigger>
                                    <AccordionContent className="space-y-4">
                                        <RenderFormFieldsComponent
                                            fields={basicFields.text_setup.items ?? []}
                                            control={control}
                                        />
                                    </AccordionContent>
                                </AccordionItem>
                            </Accordion>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="py-0">
                            <Accordion type="single" collapsible className="w-full">
                                <AccordionItem value="setup-review" className="border-b-0">
                                    <AccordionTrigger
                                        className="text-lg font-semibold text-purple-700 hover:text-purple-900">
                                        <div className="flex items-center capitalize">
                                            <Settings className="w-5 h-5 mr-2" />
                                            {common('setup_review')}
                                        </div>
                                    </AccordionTrigger>
                                    <AccordionContent className="space-y-4">
                                        <RenderFormFieldComponent
                                            item={{
                                                classNameLabel,
                                                type: 'textarea',
                                                rules: {
                                                  required: true
                                                },
                                                name: 'document.config.doc_analysis_params.contract_analysis_revision',
                                                label: documentT('analysis_rules'),
                                            }}
                                            control={control}
                                        />

                                        <DynamicInputList
                                            label={documentT('classification')}
                                            placeholder="Sin Riesgo, Poco Riesgoso, Riesgoso"
                                            control={control}
                                            name="document.config.doc_analysis_params.contract_analysis_classification"
                                            classNameLabel={classNameLabel}
                                            itemList={classificationList}
                                            setValue={setValue}
                                            inputValue={classificationInput}
                                            setInputValue={setClassificationInput}
                                        />

                                        <DynamicInputList
                                            label={common('examples')}
                                            placeholder={documentT('example_classification')}
                                            control={control}
                                            name="document.config.doc_analysis_params.contract_analysis_examples"
                                            classNameLabel={classNameLabel}
                                            itemList={examplesList}
                                            setValue={setValue}
                                            inputValue={examplesInput}
                                            setInputValue={setExamplesInput}
                                        />

                                        <RenderFormFieldComponent
                                            item={{
                                                classNameLabel,
                                                type: 'number',
                                                rules: {
                                                    required: true
                                                },
                                                name: 'document.config.doc_analysis_params.sentiment_analysis_threshold',
                                                label: common('threshold'),
                                            }}
                                            control={control}
                                        />
                                    </AccordionContent>
                                </AccordionItem>
                            </Accordion>
                        </CardContent>
                    </Card>
                </div>
            </CardContent>
        </Card>
    );
};

export default DocumentSetupComponent;